import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, FileText, Mail, Building2, Scale, Eye, Trash2, Download, AlertCircle, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

export default function LegalPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <SEO 
        title="Mentions Légales et Politique de Confidentialité"
        description="Consultez nos mentions légales, conditions d'utilisation et politique de confidentialité. DCE Express respecte vos données personnelles."
        path="/mentions-legales"
      />
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">DCE</span>
              </div>
              <span className="font-semibold text-slate-800">Mon DCE Express</span>
            </Link>
            <Link 
              to="/"
              className="hidden md:flex items-center text-slate-600 hover:text-indigo-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour à l'accueil
            </Link>
            
            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2 rounded-lg hover:bg-slate-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
          
          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-200">
              <div className="flex flex-col gap-2">
                <Link to="/" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Accueil</Link>
                <Link to="/analyse-dce" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Synthèse IA</Link>
                <Link to="/analyse-gratuite" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Analyse Gratuite</Link>
                <Link to="/blog" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Blog</Link>
                <Link to="/guide-appels-offres" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Guide</Link>
                <Link to="/contact" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
                <div className="border-t border-slate-200 mt-2 pt-4 flex flex-col gap-2 px-4">
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full">Connexion</Button>
                  </Link>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-violet-700 hover:bg-violet-800 text-white">S'inscrire</Button>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-12">
        {/* Title */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Mentions Légales & Politique de Confidentialité
          </h1>
          <p className="text-slate-600">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        {/* Quick Navigation */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-8">
          <h2 className="font-semibold text-slate-900 mb-4">Navigation rapide</h2>
          <div className="flex flex-wrap gap-3">
            {[
              { id: 'mentions', label: 'Mentions légales', icon: FileText },
              { id: 'rgpd', label: 'Protection des données', icon: Shield },
              { id: 'cookies', label: 'Cookies', icon: Eye },
              { id: 'droits', label: 'Vos droits', icon: Scale },
              { id: 'contact', label: 'Contact DPO', icon: Mail },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-indigo-100 text-slate-700 hover:text-indigo-700 rounded-lg transition-colors text-sm"
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content Sections */}
        <div className="space-y-8">
          
          {/* Section 1: Mentions Légales */}
          <section id="mentions" className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                <Building2 className="w-5 h-5 text-indigo-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">1. Mentions Légales</h2>
            </div>
            
            <div className="space-y-6 text-slate-700">
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Éditeur du site</h3>
                <p>
                  Le site <strong>Mon DCE Express</strong> (ci-après "le Site") accessible à l'adresse 
                  <strong> dce-express.fr</strong> est édité par :
                </p>
                <ul className="mt-3 space-y-1 ml-4">
                  <li><strong>Raison sociale :</strong> SOURCING AO</li>
                  <li><strong>Forme juridique :</strong> Société par Actions Simplifiée (SASU)</li>
                  <li><strong>Siège social :</strong> 220 Chemin de Crécy, 77100 Mareuil-lès-Meaux, France</li>
                  <li><strong>SIREN :</strong> 937 699 072</li>
                  <li><strong>SIRET :</strong> 937 699 072 00016</li>
                  <li><strong>N° TVA Intracommunautaire :</strong> FR34937699072</li>
                  <li><strong>Capital social :</strong> 500 €</li>
                  <li><strong>Date de création :</strong> 26 novembre 2024</li>
                  <li><strong>Code APE/NAF :</strong> 7022Z - Conseil pour les affaires et autres conseils de gestion</li>
                  <li><strong>Email :</strong> progroupement@gmail.com</li>
                  <li><strong>Présidente / Directrice de la publication :</strong> Chadia BOREHANI</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Hébergement</h3>
                <p>Le Site est hébergé par :</p>
                <ul className="mt-3 space-y-1 ml-4">
                  <li><strong>Hébergeur :</strong> Emergent Labs</li>
                  <li><strong>Adresse :</strong> Services cloud sécurisés</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Propriété intellectuelle</h3>
                <p>
                  L'ensemble du contenu du Site (textes, images, graphismes, logo, icônes, logiciels, base de données) 
                  est la propriété exclusive de SOURCING AO ou de ses partenaires et est protégé par les lois 
                  françaises et internationales relatives à la propriété intellectuelle.
                </p>
                <p className="mt-2">
                  Toute reproduction, représentation, modification, publication, adaptation de tout ou partie des 
                  éléments du Site, quel que soit le moyen ou le procédé utilisé, est interdite, sauf autorisation 
                  écrite préalable de SOURCING AO.
                </p>
              </div>
            </div>
          </section>

          {/* Section 2: RGPD - Protection des données */}
          <section id="rgpd" className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">2. Protection des Données Personnelles (RGPD)</h2>
            </div>
            
            <div className="space-y-6 text-slate-700">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <p className="text-emerald-800">
                  <strong>Engagement :</strong> Mon DCE Express s'engage à protéger vos données personnelles 
                  conformément au Règlement Général sur la Protection des Données (RGPD) - Règlement (UE) 2016/679 
                  et à la loi Informatique et Libertés du 6 janvier 1978 modifiée.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-slate-900 mb-2">2.1 Responsable du traitement</h3>
                <p>
                  Le responsable du traitement des données personnelles est SOURCING AO, SASU au capital de 500€,
                  immatriculée au RCS de Meaux sous le numéro 937 699 072, 
                  joignable à l'adresse : <strong>progroupement@gmail.com</strong>
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-slate-900 mb-2">2.2 Données collectées</h3>
                <p>Nous collectons les données suivantes :</p>
                <ul className="mt-3 space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-indigo-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>Données d'identification :</strong> nom, prénom, adresse email, numéro de téléphone</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-indigo-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>Données professionnelles :</strong> nom de l'entreprise, secteur d'activité, région, mots-clés métier</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-indigo-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>Données de connexion :</strong> adresse IP (hashée), logs de connexion, préférences utilisateur</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-indigo-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>Données de paiement :</strong> traitées de manière sécurisée par notre prestataire Stripe (nous ne stockons pas vos données bancaires)</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-slate-900 mb-2">2.3 Finalités du traitement</h3>
                <p>Vos données sont collectées pour les finalités suivantes :</p>
                <table className="mt-3 w-full border-collapse">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="border border-slate-200 px-4 py-2 text-left">Finalité</th>
                      <th className="border border-slate-200 px-4 py-2 text-left">Base légale</th>
                      <th className="border border-slate-200 px-4 py-2 text-left">Durée de conservation</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-slate-200 px-4 py-2">Gestion de votre compte</td>
                      <td className="border border-slate-200 px-4 py-2">Exécution du contrat</td>
                      <td className="border border-slate-200 px-4 py-2">Durée du compte + 3 ans</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-200 px-4 py-2">Recommandations IA personnalisées</td>
                      <td className="border border-slate-200 px-4 py-2">Exécution du contrat</td>
                      <td className="border border-slate-200 px-4 py-2">Durée du compte</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-200 px-4 py-2">Envoi d'alertes email</td>
                      <td className="border border-slate-200 px-4 py-2">Consentement</td>
                      <td className="border border-slate-200 px-4 py-2">Jusqu'au retrait du consentement</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-200 px-4 py-2">Facturation et paiements</td>
                      <td className="border border-slate-200 px-4 py-2">Obligation légale</td>
                      <td className="border border-slate-200 px-4 py-2">10 ans (obligation comptable)</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-200 px-4 py-2">Amélioration des services</td>
                      <td className="border border-slate-200 px-4 py-2">Intérêt légitime</td>
                      <td className="border border-slate-200 px-4 py-2">26 mois (anonymisé)</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div>
                <h3 className="font-semibold text-slate-900 mb-2">2.4 Destinataires des données</h3>
                <p>Vos données peuvent être transmises aux destinataires suivants :</p>
                <ul className="mt-3 space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-indigo-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>Stripe :</strong> pour le traitement sécurisé des paiements (certifié PCI-DSS)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-indigo-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>Resend :</strong> pour l'envoi d'emails transactionnels</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-indigo-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>OpenAI :</strong> pour les recommandations IA (données anonymisées)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-indigo-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>MongoDB Atlas :</strong> pour le stockage sécurisé des données (serveurs en UE)</span>
                  </li>
                </ul>
                <p className="mt-3 text-sm text-slate-600">
                  Aucune donnée n'est vendue à des tiers. Les transferts hors UE sont encadrés par des clauses 
                  contractuelles types approuvées par la Commission européenne.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-slate-900 mb-2">2.5 Sécurité des données</h3>
                <p>Nous mettons en œuvre les mesures de sécurité suivantes :</p>
                <ul className="mt-3 space-y-1 ml-4">
                  <li>• Chiffrement SSL/TLS pour toutes les communications</li>
                  <li>• Hashage des mots de passe (bcrypt)</li>
                  <li>• Authentification sécurisée par tokens JWT</li>
                  <li>• Accès restreint aux données (principe du moindre privilège)</li>
                  <li>• Sauvegardes régulières et chiffrées</li>
                  <li>• Surveillance et détection des intrusions</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 3: Vos Droits */}
          <section id="droits" className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <Scale className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">3. Vos Droits</h2>
            </div>
            
            <div className="space-y-6 text-slate-700">
              <p>
                Conformément au RGPD, vous disposez des droits suivants sur vos données personnelles :
              </p>

              <div className="grid md:grid-cols-2 gap-4">
                {[
                  {
                    icon: Eye,
                    title: "Droit d'accès",
                    desc: "Obtenir la confirmation que vos données sont traitées et en recevoir une copie.",
                    color: "blue"
                  },
                  {
                    icon: FileText,
                    title: "Droit de rectification",
                    desc: "Corriger vos données inexactes ou les compléter si elles sont incomplètes.",
                    color: "green"
                  },
                  {
                    icon: Trash2,
                    title: "Droit à l'effacement",
                    desc: "Demander la suppression de vos données dans certaines conditions.",
                    color: "red"
                  },
                  {
                    icon: AlertCircle,
                    title: "Droit d'opposition",
                    desc: "Vous opposer au traitement de vos données pour des motifs légitimes.",
                    color: "orange"
                  },
                  {
                    icon: Download,
                    title: "Droit à la portabilité",
                    desc: "Recevoir vos données dans un format structuré et lisible par machine.",
                    color: "purple"
                  },
                  {
                    icon: Shield,
                    title: "Droit à la limitation",
                    desc: "Limiter le traitement de vos données dans certains cas.",
                    color: "indigo"
                  }
                ].map((right, i) => (
                  <div key={`item-${i}`} className={`bg-${right.color}-50 border border-${right.color}-200 rounded-xl p-4`}>
                    <div className="flex items-center gap-2 mb-2">
                      <right.icon className={`w-5 h-5 text-${right.color}-600`} />
                      <h4 className="font-semibold text-slate-900">{right.title}</h4>
                    </div>
                    <p className="text-sm text-slate-600">{right.desc}</p>
                  </div>
                ))}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <h4 className="font-semibold text-amber-800 mb-2">Comment exercer vos droits ?</h4>
                <p className="text-amber-700">
                  Vous pouvez exercer vos droits à tout moment en nous contactant :
                </p>
                <ul className="mt-2 text-amber-700">
                  <li>• Par email : <strong>progroupement@gmail.com</strong></li>
                  <li>• Via la page <Link to="/contact" className="underline">Contact</Link></li>
                  <li>• Depuis les paramètres de votre compte</li>
                </ul>
                <p className="mt-2 text-sm text-amber-600">
                  Nous répondrons à votre demande dans un délai de 30 jours maximum.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Réclamation auprès de la CNIL</h3>
                <p>
                  Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation 
                  auprès de la Commission Nationale de l'Informatique et des Libertés (CNIL) :
                </p>
                <ul className="mt-2 ml-4">
                  <li>• Site web : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">www.cnil.fr</a></li>
                  <li>• Adresse : 3 Place de Fontenoy - TSA 80715 - 75334 PARIS CEDEX 07</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 4: Cookies */}
          <section id="cookies" className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                <Eye className="w-5 h-5 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">4. Politique de Cookies</h2>
            </div>
            
            <div className="space-y-6 text-slate-700">
              <p>
                Un cookie est un petit fichier texte déposé sur votre appareil lors de la visite d'un site web. 
                Mon DCE Express utilise des cookies pour assurer le bon fonctionnement du site et améliorer votre expérience.
              </p>

              <div>
                <h3 className="font-semibold text-slate-900 mb-3">Types de cookies utilisés</h3>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="border border-slate-200 px-4 py-2 text-left">Type</th>
                      <th className="border border-slate-200 px-4 py-2 text-left">Finalité</th>
                      <th className="border border-slate-200 px-4 py-2 text-left">Durée</th>
                      <th className="border border-slate-200 px-4 py-2 text-left">Consentement</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-slate-200 px-4 py-2 font-medium">Essentiels</td>
                      <td className="border border-slate-200 px-4 py-2">Authentification, sécurité, préférences</td>
                      <td className="border border-slate-200 px-4 py-2">Session / 30 jours</td>
                      <td className="border border-slate-200 px-4 py-2 text-green-600">Non requis</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-200 px-4 py-2 font-medium">Fonctionnels</td>
                      <td className="border border-slate-200 px-4 py-2">Mémorisation des choix utilisateur</td>
                      <td className="border border-slate-200 px-4 py-2">1 an</td>
                      <td className="border border-slate-200 px-4 py-2 text-green-600">Non requis</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-200 px-4 py-2 font-medium">Analytiques</td>
                      <td className="border border-slate-200 px-4 py-2">Mesure d'audience anonyme</td>
                      <td className="border border-slate-200 px-4 py-2">13 mois</td>
                      <td className="border border-slate-200 px-4 py-2 text-amber-600">Requis</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Gestion des cookies</h3>
                <p>
                  Vous pouvez à tout moment gérer vos préférences de cookies :
                </p>
                <ul className="mt-2 ml-4">
                  <li>• Via les paramètres de votre navigateur</li>
                  <li>• En supprimant les cookies existants</li>
                </ul>
                <p className="mt-2 text-sm text-slate-600">
                  Note : La désactivation de certains cookies peut affecter le fonctionnement du site.
                </p>
              </div>
            </div>
          </section>

          {/* Section 5: Contact DPO */}
          <section id="contact" className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                <Mail className="w-5 h-5 text-indigo-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">5. Contact</h2>
            </div>
            
            <div className="space-y-6 text-slate-700">
              <p>
                Pour toute question relative à cette politique de confidentialité ou pour exercer vos droits, 
                vous pouvez nous contacter :
              </p>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-xl p-6">
                  <h4 className="font-semibold text-slate-900 mb-3">Délégué à la Protection des Données (DPO)</h4>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-indigo-600" />
                      <span>progroupement@gmail.com</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-indigo-600" />
                      <span>SOURCING AO - 220 Chemin de Crécy, 77100 Mareuil-lès-Meaux</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-indigo-50 rounded-xl p-6">
                  <h4 className="font-semibold text-slate-900 mb-3">Formulaire de contact</h4>
                  <p className="text-sm text-slate-600 mb-4">
                    Utilisez notre formulaire en ligne pour une réponse rapide.
                  </p>
                  <Link 
                    to="/contact"
                    className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Nous contacter
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Section 6: Conditions Générales d'Utilisation */}
          <section className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-slate-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">6. Conditions Générales d'Utilisation</h2>
            </div>
            
            <div className="space-y-6 text-slate-700">
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">6.1 Objet</h3>
                <p>
                  Les présentes Conditions Générales d'Utilisation (CGU) régissent l'accès et l'utilisation 
                  du site Mon DCE Express et de ses services de recherche et recommandation de marchés publics.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-slate-900 mb-2">6.2 Services proposés</h3>
                <p>Mon DCE Express propose les services suivants :</p>
                <ul className="mt-2 ml-4 space-y-1">
                  <li>• Agrégation de marchés publics depuis sources officielles (BOAMP, TED, DECP, etc.)</li>
                  <li>• Recommandations personnalisées par intelligence artificielle</li>
                  <li>• Alertes email sur les nouveaux marchés correspondant à votre profil</li>
                  <li>• Service de récupération de DCE (Dossier de Consultation des Entreprises)</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-slate-900 mb-2">6.3 Responsabilité</h3>
                <p>
                  Mon DCE Express s'efforce de fournir des informations exactes et à jour. Toutefois :
                </p>
                <ul className="mt-2 ml-4 space-y-1">
                  <li>• Les données des marchés proviennent de sources tierces et peuvent contenir des erreurs</li>
                  <li>• Les recommandations IA sont indicatives et ne garantissent pas le succès aux appels d'offres</li>
                  <li>• L'utilisateur reste seul responsable de ses décisions de candidature</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-slate-900 mb-2">6.4 Modification des CGU</h3>
                <p>
                  Nous nous réservons le droit de modifier les présentes CGU à tout moment. 
                  Les utilisateurs seront informés par email des modifications substantielles.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-slate-900 mb-2">6.5 Droit applicable</h3>
                <p>
                  Les présentes CGU sont soumises au droit français. En cas de litige, les tribunaux 
                  français seront seuls compétents.
                </p>
              </div>
            </div>
          </section>

        </div>

        {/* Back to top */}
        <div className="text-center mt-12">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="inline-flex items-center px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2 rotate-90" />
            Retour en haut
          </button>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8 mt-16">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p>© 2024-{new Date().getFullYear()} Mon DCE Express - Tous droits réservés</p>
          <p className="mt-2 text-sm">
            <Link to="/contact" className="hover:text-white">Contact</Link>
            {' • '}
            <Link to="/legal" className="hover:text-white">Mentions légales</Link>
          </p>
        </div>
      </footer>
    </div>
    </>
  );
}
